import { getFirestore, writeBatch, doc, Timestamp, enableIndexedDbPersistence } from 'firebase/firestore';
import Dexie from 'dexie/dist/dexie';

// Initialize Dexie for IndexedDB queue management
const db = new Dexie('LogQueue');
db.version(1).stores({ 
  logs: '++id, sessionId, timestamp, userId' 
});

// Environment configuration
const ENV = process.env.REACT_APP_ENVIRONMENT || 'DEV';
const ENV_COLLECTION = `client_logs_${ENV.toUpperCase()}`;

// Rate limiting configuration - OPTIMIZED FOR LOW MEMORY USAGE
const WRITE_LIMIT_PER_MINUTE = 50; // Reduced from 500
const FLUSH_INTERVAL = 60000; // Increased from 30s to 60s
const BATCH_SIZE = 100; // Reduced from 500 
const MAX_QUEUE_SIZE = 1000; // Reduced from 10000
const RETRY_ATTEMPTS = 2; // Reduced from 3
const TTL_DAYS = 7; // Reduced from 90 days (memory conscious)

// ---------------------------------------------------------------------------
// Logging ON/OFF controls
// CRITICAL: Logging is DISABLED BY DEFAULT to prevent memory leaks
// Build-time master switch — set REACT_APP_LOGGING_ENABLED=true in .env to enable.
// Runtime override — in browser console (no redeploy needed):
//   Turn OFF: localStorage.setItem('nd_logging_enabled', 'false')
//   Turn ON:  localStorage.setItem('nd_logging_enabled', 'true')
//   Reset to env default: localStorage.removeItem('nd_logging_enabled')
//
// Per-event-type control (runtime, browser console):
//   localStorage.setItem('nd_log_event_types', 'error')
//   localStorage.removeItem('nd_log_event_types')  ← resets to env default
// ---------------------------------------------------------------------------
const BUILD_LOGGING_ENABLED = process.env.REACT_APP_LOGGING_ENABLED === 'true'
  ? true
  : false; // DEFAULT DISABLED - prevents memory leaks
const BUILD_LOG_EVENT_TYPES = (process.env.REACT_APP_LOG_EVENT_TYPES || '')
  .split(',')
  .map(t => t.trim())
  .filter(Boolean); // empty array = no types logged (safest for memory)

class Logger {
  constructor() {
    this.sessionId = 'sess_' + crypto.randomUUID();
    this.userId = this.generateAnonymousUserId();
    this.firestore = null;
    this.flushTimer = null;
    this.writeTimestamps = [];
    this.retryCount = 0;
    this.version = process.env.REACT_APP_VERSION || '1.0.0';
    this.isInitialized = false;
    
    this.initialize();
  }

  async initialize() {
    // Do nothing if logging is disabled at build-time and not overridden at runtime
    if (!this.isLoggingEnabled()) {
      console.log('[Logger] Logging is DISABLED by default — no memory overhead.');
      this.isInitialized = false; // Mark as not initialized to skip async operations
      return;
    }

    try {
      // Get Firestore instance only if logging is enabled
      this.firestore = getFirestore();
      
      // Enable offline persistence with error handling
      await enableIndexedDbPersistence(this.firestore).catch((err) => {
        if (err.code === 'failed-precondition') {
          console.warn('Multiple tabs open, persistence enabled in first tab only');
        } else if (err.code === 'unimplemented') {
          console.warn('Browser does not support persistence');
        }
      });

      this.isInitialized = true;
      
      // Start periodic flush with longer interval for memory conservation
      this.startPeriodicFlush();
      
      // Handle page unload - flush remaining logs
      window.addEventListener('beforeunload', () => this.flush());
      
      console.log('Logger initialized (logging ENABLED)', { 
        sessionId: this.sessionId, 
        environment: ENV 
      });
    } catch (error) {
      console.error('Logger initialization failed:', error);
      this.isInitialized = false;
    }
  }

  getSessionId() {
    return this.sessionId;
  }

  generateAnonymousUserId() {
    // Use actual user ID from localStorage if available
    const userId = localStorage.getItem('in_userid');
    if (userId) {
      return userId;
    }
    // Fallback to anonymous if not logged in
    const username = localStorage.getItem('in_username') || 'anonymous';
    const fingerprint = `${navigator.userAgent}_${username}`;
    return 'anon_' + btoa(fingerprint).substring(0, 20);
  }

  getUserConsent() {
    // Logging enabled by default
    // Set consent in localStorage if not already set
    if (!localStorage.getItem('userConsent')) {
      localStorage.setItem('userConsent', 'true');
    }
    return true;
  }

  // ---------------------------------------------------------------------------
  // Logging ON/OFF control methods
  // ---------------------------------------------------------------------------

  /**
   * Check if logging is currently enabled.
   * Runtime localStorage value takes precedence over the build-time env var.
   */
  isLoggingEnabled() {
    const runtimeValue = localStorage.getItem('nd_logging_enabled');
    if (runtimeValue !== null) {
      return runtimeValue === 'true';
    }
    return BUILD_LOGGING_ENABLED;
  }

  /**
   * Check if a specific event type is allowed to be logged.
   * Runtime localStorage value takes precedence over the build-time env var.
   * An empty allowed list means ALL event types are permitted.
   */
  isEventTypeAllowed(eventType) {
    const runtimeTypes = localStorage.getItem('nd_log_event_types');
    const allowedTypes = runtimeTypes !== null
      ? runtimeTypes.split(',').map(t => t.trim()).filter(Boolean)
      : BUILD_LOG_EVENT_TYPES;

    return allowedTypes.length === 0 || allowedTypes.includes(eventType);
  }

  /**
   * Enable all logging at runtime (persists across page refreshes).
   * Usage from browser console: __nd_logger.enable()
   */
  enable() {
    localStorage.setItem('nd_logging_enabled', 'true');
    console.log('[Logger] Logging ENABLED');
    // Re-initialize if it was skipped due to logging being off at startup
    if (!this.isInitialized) {
      this.initialize();
    }
  }

  /**
   * Disable all logging at runtime (persists across page refreshes).
   * Usage from browser console: __nd_logger.disable()
   */
  disable() {
    localStorage.setItem('nd_logging_enabled', 'false');
    // Stop the flush timer immediately
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    console.log('[Logger] Logging DISABLED — flush timer stopped.');
  }

  /**
   * Print current logging status to the console.
   * Usage from browser console: __nd_logger.status()
   */
  status() {
    const runtimeTypes = localStorage.getItem('nd_log_event_types');
    const allowedTypes = runtimeTypes !== null
      ? runtimeTypes.split(',').map(t => t.trim()).filter(Boolean)
      : BUILD_LOG_EVENT_TYPES;

    console.table({
      'Logging enabled': this.isLoggingEnabled(),
      'Source': localStorage.getItem('nd_logging_enabled') !== null ? 'runtime (localStorage)' : 'build-time (.env)',
      'Allowed event types': allowedTypes.length === 0 ? 'ALL' : allowedTypes.join(', '),
      'Event types source': runtimeTypes !== null ? 'runtime (localStorage)' : 'build-time (.env)',
    });
  }

  /**
   * Set which event types to log at runtime.
   * Pass an array of event type strings, or empty array for ALL.
   * Usage: __nd_logger.setEventTypes(['error', 'api_start'])
   *        __nd_logger.setEventTypes([])  // log everything
   */
  setEventTypes(types = []) {
    if (types.length === 0) {
      localStorage.removeItem('nd_log_event_types');
      console.log('[Logger] Event type filter cleared — logging ALL event types');
    } else {
      localStorage.setItem('nd_log_event_types', types.join(','));
      console.log('[Logger] Now logging only:', types.join(', '));
    }
  }

  /**
   * Reset both ON/OFF and event-type filters to the build-time .env defaults.
   * Usage from browser console: __nd_logger.reset()
   */
  reset() {
    localStorage.removeItem('nd_logging_enabled');
    localStorage.removeItem('nd_log_event_types');
    console.log('[Logger] Reset to build-time defaults. Logging enabled:', BUILD_LOGGING_ENABLED);
  }

  getPlatform() {
    const ua = navigator.userAgent;
    let browser = 'unknown';
    let version = '';
    
    // Detect Chrome
    const chromeMatch = ua.match(/Chrome\/(\d+)/);
    if (chromeMatch && ua.indexOf('Edg') === -1) {
      browser = 'chrome';
      version = chromeMatch[1];
    }
    // Detect Edge
    else if (ua.indexOf('Edg') > -1) {
      browser = 'edge';
      const edgeMatch = ua.match(/Edg\/(\d+)/);
      version = edgeMatch ? edgeMatch[1] : '';
    }
    // Detect Firefox
    else if (ua.indexOf('Firefox') > -1) {
      browser = 'firefox';
      const firefoxMatch = ua.match(/Firefox\/(\d+)/);
      version = firefoxMatch ? firefoxMatch[1] : '';
    }
    // Detect Safari
    else if (ua.indexOf('Safari') > -1 && ua.indexOf('Chrome') === -1) {
      browser = 'safari';
      const safariMatch = ua.match(/Version\/(\d+)/);
      version = safariMatch ? safariMatch[1] : '';
    }
    
    return version ? `web-${browser}-${version}` : `web-${browser}`;
  }

  getLogPath(timestamp, userId) {
    const date = new Date(timestamp).toISOString().slice(0, 10);
    const year = date.slice(0, 4);
    const month = date.slice(5, 7);
    const day = date.slice(8, 10);
    return `${ENV_COLLECTION}/${year}/${month}/${day}/${userId}`;
  }

  async logEvent(eventType, metadata = {}) {
    // --- Master ON/OFF gate ---
    if (!this.isLoggingEnabled()) {
      return;
    }

    // --- Per-event-type gate ---
    if (!this.isEventTypeAllowed(eventType)) {
      return;
    }

    // Check consent
    if (!this.getUserConsent()) {
      return;
    }

    if (!this.isInitialized) {
      console.warn('Logger not initialized yet');
      return;
    }

    try {
      const now = new Date();
      const expireAt = new Date(now.getTime() + TTL_DAYS * 24 * 60 * 60 * 1000);
      
      // Get current userId from localStorage (dynamic, not cached)
      const currentUserId = localStorage.getItem('in_userid') || this.userId;
      
      const logEntry = {
        sessionId: this.sessionId,
        userId: currentUserId,
        app: 'fieldoperation',
        version: this.version,
        platform: this.getPlatform(),
        timestamp: now.toISOString(),
        eventType: eventType,
        metadata: metadata,
        userConsent: true,
        expireAt: expireAt.toISOString()
      };

      // Add to IndexedDB queue
      await db.logs.add(logEntry);
      
      // Check queue size and prune if necessary
      const count = await db.logs.count();
      if (count > MAX_QUEUE_SIZE) {
        await this.pruneQueue();
      }

      // Throttle check and flush if needed
      if (this.shouldFlush()) {
        await this.flush();
      }
    } catch (error) {
      console.error('Error logging event:', error);
    }
  }

  async pruneQueue() {
    // Keep only the latest 10,000 entries
    const allLogs = await db.logs.orderBy('id').toArray();
    const toDelete = allLogs.slice(0, allLogs.length - MAX_QUEUE_SIZE);
    await db.logs.bulkDelete(toDelete.map(log => log.id));
    console.warn('Queue pruned, removed', toDelete.length, 'old entries');
  }

  shouldFlush() {
    return !this.isThrottled();
  }

  isThrottled() {
    // Remove timestamps older than 1 minute
    const oneMinuteAgo = Date.now() - 60000;
    this.writeTimestamps = this.writeTimestamps.filter(ts => ts > oneMinuteAgo);
    
    // Check if we're under the limit
    return this.writeTimestamps.length >= WRITE_LIMIT_PER_MINUTE;
  }

  startPeriodicFlush() {
    if (!this.isLoggingEnabled()) {
      return;
    }

    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }
    
    this.flushTimer = setInterval(() => {
      this.flush();
    }, FLUSH_INTERVAL);
  }

  async flush() {
    // Do not flush to Firebase if logging is disabled
    if (!this.isLoggingEnabled()) {
      return;
    }

    if (!this.isInitialized || !this.firestore) {
      return;
    }

    try {
      // Get pending logs from IndexedDB
      const pending = await db.logs.limit(BATCH_SIZE).toArray();
      
      if (pending.length === 0) {
        return;
      }

      // Check throttling
      if (this.isThrottled()) {
        console.warn('Write throttled, will retry later');
        return;
      }

      // Create batch write
      const batch = writeBatch(this.firestore);
      const processedIds = [];

      pending.forEach(log => {
        const logPath = this.getLogPath(log.timestamp, log.userId);
        // Ensure timestamp is a string
        const timestampStr = typeof log.timestamp === 'string' ? log.timestamp : new Date(log.timestamp).toISOString();
        const docId = timestampStr.replace(/[:.]/g, '-').replace('Z', '') + '_' + crypto.randomUUID().substring(0, 8);
        const docRef = doc(this.firestore, logPath, docId);

        // Sanitize metadata to remove nested arrays before storing
        const sanitizedMetadata = this.sanitizePayloadForFirestore(log.metadata || {});
        
        // Convert ISO strings back to Firestore Timestamps for storage
        const firestoreLog = {
          ...log,
          metadata: sanitizedMetadata,
          timestamp: Timestamp.fromDate(new Date(log.timestamp)),
          expireAt: Timestamp.fromDate(new Date(log.expireAt))
        };
        
        // Remove IndexedDB id
        delete firestoreLog.id;
        
        batch.set(docRef, firestoreLog);
        processedIds.push(log.id);
      });

      // Commit batch
      await batch.commit();
      
      // Track write timestamp
      this.writeTimestamps.push(Date.now());
      
      // Remove successfully written logs from IndexedDB
      await db.logs.bulkDelete(processedIds);
      
      console.log(`Successfully flushed ${pending.length} logs to Firestore`);
      
      // Reset retry count on success
      this.retryCount = 0;
      
    } catch (error) {
      console.error('Error flushing logs:', error);
      await this.retryLater(error);
    }
  }

  async retryLater(error) {
    if (this.retryCount >= RETRY_ATTEMPTS) {
      console.error('Max retry attempts reached, giving up');
      this.retryCount = 0;
      return;
    }

    this.retryCount++;
    const delay = Math.min(1000 * Math.pow(2, this.retryCount - 1), 60000); // 1s, 2s, 4s... max 1min
    const jitter = Math.random() * 1000; // Add jitter
    
    console.log(`Retrying flush in ${delay + jitter}ms (attempt ${this.retryCount}/${RETRY_ATTEMPTS})`);
    
    setTimeout(() => {
      this.flush();
    }, delay + jitter);
  }

  // Helper method to sanitize payload for Firestore (no nested arrays)
  sanitizePayloadForFirestore(data, depth = 0, maxDepth = 5) {
    // Prevent infinite recursion
    if (depth > maxDepth) {
      return '[Object: Max depth exceeded]';
    }

    // Handle null/undefined
    if (data === null || data === undefined) {
      return data;
    }

    // Handle primitives
    if (typeof data !== 'object') {
      // Truncate very long strings
      if (typeof data === 'string' && data.length > 10000) {
        return data.substring(0, 10000) + '... [truncated]';
      }
      return data;
    }

    // Handle arrays - convert nested arrays to JSON strings
    if (Array.isArray(data)) {
      return data.map((item, index) => {
        if (Array.isArray(item)) {
          // Nested array detected - convert to JSON string
          try {
            return JSON.stringify(item);
          } catch (e) {
            return '[Array: Unable to stringify]';
          }
        } else if (typeof item === 'object' && item !== null) {
          return this.sanitizePayloadForFirestore(item, depth + 1, maxDepth);
        }
        return item;
      });
    }

    // Handle objects
    const sanitized = {};
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        const value = data[key];
        
        if (Array.isArray(value)) {
          // Check if it contains nested arrays
          const hasNestedArray = value.some(item => Array.isArray(item));
          if (hasNestedArray) {
            // Convert the entire array to JSON string
            try {
              sanitized[key] = JSON.stringify(value);
            } catch (e) {
              sanitized[key] = '[Array: Unable to stringify]';
            }
          } else {
            // Safe array - recursively sanitize objects inside
            sanitized[key] = value.map(item => {
              if (typeof item === 'object' && item !== null && !Array.isArray(item)) {
                return this.sanitizePayloadForFirestore(item, depth + 1, maxDepth);
              }
              return item;
            });
          }
        } else if (typeof value === 'object' && value !== null) {
          sanitized[key] = this.sanitizePayloadForFirestore(value, depth + 1, maxDepth);
        } else if (typeof value === 'string' && value.length > 10000) {
          sanitized[key] = value.substring(0, 10000) + '... [truncated]';
        } else {
          sanitized[key] = value;
        }
      }
    }
    return sanitized;
  }

  // Public methods for logging specific events
  
  /**
   * Log click event
   * @param {string} elementId - ID of the clicked element
   * @param {string} elementClass - CSS class of the clicked element
   * @param {string} pageUrl - Current page URL
   * @param {number} x - Click X coordinate
   * @param {number} y - Click Y coordinate
   * 
   * Schema: {
   *   sessionId, userId, app, version, platform, timestamp,
   *   eventType: 'click',
   *   metadata: { elementId, elementClass, pageUrl, x, y },
   *   userConsent, expireAt
   * }
   */
  logClick(elementId, elementClass, pageUrl, x, y) {
    this.logEvent('click', {
      elementId: elementId || '',
      elementClass: elementClass || '',
      pageUrl: pageUrl || window.location.pathname,
      x: x || 0,
      y: y || 0
    });
  }

  /**
   * Log page view / navigation event
   * @param {string} fromUrl - Previous page URL
   * @param {string} toUrl - Current page URL
   * @param {number} durationMs - Time spent on previous page (milliseconds)
   * @param {string} referrer - Referrer URL
   * 
   * Schema: {
   *   sessionId, userId, app, version, platform, timestamp,
   *   eventType: 'page_view',
   *   metadata: { fromUrl, toUrl, durationMs, referrer },
   *   userConsent, expireAt
   * }
   */
  logPageView(fromUrl, toUrl, durationMs, referrer) {
    this.logEvent('page_view', {
      fromUrl: fromUrl || '',
      toUrl: toUrl || window.location.pathname,
      durationMs: durationMs || 0,
      referrer: referrer || document.referrer || ''
    });
  }

  /**
   * Log API request start
   * @param {string} url - API endpoint URL
   * @param {string} method - HTTP method (GET, POST, PUT, DELETE, etc.)
   * @param {string} pageUrl - Current page URL where request was initiated
   * @param {Object|string} payload - The decrypted payload being sent (optional)
   * @param {string} contentType - Content type of the payload (optional)
   * 
   * Schema: {
   *   sessionId, userId, app, version, platform, timestamp,
   *   eventType: 'api_start',
   *   metadata: { url, method, pageUrl, payload, contentType },
   *   userConsent, expireAt
   * }
   */
  logApiStart(url, method, pageUrl, payload, contentType) {
    try {
      const metadata = {
        url: url || '',
        method: method || 'GET',
        pageUrl: pageUrl || window.location.pathname
      };

      // Add payload if provided
      if (payload !== undefined && payload !== null) {
        // Sanitize and prepare payload for logging
        let sanitizedPayload;
        if (typeof payload === 'string') {
          try {
            const parsed = JSON.parse(payload);
            // Sanitize parsed JSON to remove nested arrays
            sanitizedPayload = this.sanitizePayloadForFirestore(parsed);
          } catch (e) {
            // If parsing fails, truncate long strings
            sanitizedPayload = payload.length > 10000 
              ? payload.substring(0, 10000) + '... [truncated]' 
              : payload;
          }
        } else if (payload instanceof FormData) {
          // Convert FormData to object for logging
          sanitizedPayload = {};
          for (const [key, value] of payload.entries()) {
            if (value instanceof File) {
              sanitizedPayload[key] = `[File: ${value.name}, Size: ${value.size}, Type: ${value.type}]`;
            } else {
              sanitizedPayload[key] = value;
            }
          }
        } else {
          // Sanitize object/array payloads
          sanitizedPayload = this.sanitizePayloadForFirestore(payload);
        }
        metadata.payload = sanitizedPayload;
        metadata.contentType = contentType || 'unknown';
      }

      this.logEvent('api_start', metadata);
    } catch (error) {
      console.error('Error logging API start:', error);
    }
  }

  /**
   * Log API request completion
   * @param {string} url - API endpoint URL
   * @param {string} method - HTTP method (GET, POST, PUT, DELETE, etc.)
   * @param {number} status - HTTP status code (200, 404, 500, etc.)
   * @param {number} durationMs - Request duration in milliseconds
   * @param {string|null} error - Error message if request failed, null otherwise
   * 
   * Schema: {
   *   sessionId, userId, app, version, platform, timestamp,
   *   eventType: 'api_end',
   *   metadata: { url, method, status, durationMs, error },
   *   userConsent, expireAt
   * }
   */
  logApiEnd(url, method, status, durationMs, error) {
    this.logEvent('api_end', {
      url: url || '',
      method: method || 'GET',
      status: status || 0,
      durationMs: durationMs || 0,
      error: error || null
    });
  }

  /**
   * Log JavaScript error
   * @param {string} message - Error message
   * @param {string} stack - Error stack trace
   * @param {string} url - URL where error occurred
   * @param {number} lineNumber - Line number of error
   * @param {number} columnNumber - Column number of error
   * 
   * Schema: {
   *   sessionId, userId, app, version, platform, timestamp,
   *   eventType: 'error',
   *   metadata: { message, stack, url, lineNumber, columnNumber },
   *   userConsent, expireAt
   * }
   */
  logError(message, stack, url, lineNumber, columnNumber) {
    this.logEvent('error', {
      message: message || 'Unknown error',
      stack: stack || '',
      url: url || window.location.pathname,
      lineNumber: lineNumber || 0,
      columnNumber: columnNumber || 0
    });
  }

  /**
   * Log authentication event
   * @param {string} action - Auth action: 'login', 'logout', 'register', etc.
   * @param {boolean} success - Whether auth action succeeded
   * @param {string} userId - User identifier
   * @param {string} method - Auth method: 'email', 'oauth', 'otp', etc.
   * 
   * Schema: {
   *   sessionId, userId, app, version, platform, timestamp,
   *   eventType: 'auth_login' | 'auth_logout' | 'auth_register',
   *   metadata: { success, userId, method },
   *   userConsent, expireAt
   * }
   */
  logAuth(action, success, userId, method) {
    this.logEvent('auth_' + action, {
      success: success || false,
      userId: userId || '',
      method: method || ''
    });
  }

  /**
   * Log form submission
   * @param {string} formId - Form element ID
   * @param {string} formName - Form name or identifier
   * @param {string} pageUrl - Current page URL
   * @param {number} fieldCount - Number of fields in the form
   * 
   * Schema: {
   *   sessionId, userId, app, version, platform, timestamp,
   *   eventType: 'form_submit',
   *   metadata: { formId, formName, pageUrl, fieldCount },
   *   userConsent, expireAt
   * }
   */
  logFormSubmit(formId, formName, pageUrl, fieldCount) {
    this.logEvent('form_submit', {
      formId: formId || '',
      formName: formName || '',
      pageUrl: pageUrl || window.location.pathname,
      fieldCount: fieldCount || 0
    });
  }

  destroy() {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }
    this.flush(); // Final flush
  }
}

// Create singleton instance
const logger = new Logger();

// Expose on window so logging can be controlled live from the browser console:
//   __nd_logger.enable()                              → turn ON all logging
//   __nd_logger.disable()                             → turn OFF all logging
//   __nd_logger.setEventTypes(['error','api_start'])  → log only these event types
//   __nd_logger.setEventTypes([])                     → log ALL event types
//   __nd_logger.status()                              → print current settings
//   __nd_logger.reset()                               → revert to build-time .env defaults
if (typeof window !== 'undefined') {
  window.__nd_logger = logger;
}

// Export for use in the app
export default logger;
